import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment-timezone";

import WalletApi from "Services/Wallet";
import { hideLoader, showLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import "./PaiementPage.css";

const firstValue = (...values) =>
  values.find((value) => value !== null && value !== undefined && String(value).trim() !== "") ?? "--";

const formatDateTime = (value, timezone) => {
  if (!value) return "--";
  const date = moment(value);
  if (!date.isValid()) return "--";
  return date.tz(timezone || "Africa/Douala").format("DD MMM YYYY HH:mm");
};

const formatAmount = (value, currencyCode = "") => {
  if (value === null || value === undefined || value === "") {
    return `--${currencyCode ? ` ${currencyCode}` : ""}`;
  }

  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return `${value}${currencyCode ? ` ${currencyCode}` : ""}`;
  }

  return `${new Intl.NumberFormat("fr-FR").format(amount)}${currencyCode ? ` ${currencyCode}` : ""}`;
};

const normalizeType = (value) => String(value || "").trim().toLowerCase();

const getTransactionTone = (entryType, intl) => {
  const normalized = normalizeType(entryType);
  if (normalized.includes("debit")) {
    return {
      color: "#d92d20",
      background: "#fff1f1",
      label: intl.formatMessage({ id: "dashboard.transactions.type.debit" }),
      direction: "down",
    };
  }

  if (normalized.includes("credit")) {
    return {
      color: "#0b7a45",
      background: "#eaf8ed",
      label: intl.formatMessage({ id: "dashboard.transactions.type.credit" }),
      direction: "up",
    };
  }

  return {
    color: "#10233f",
    background: "#eef2f7",
    label: String(firstValue(entryType, "--")).toUpperCase(),
    direction: null,
  };
};

const normalizeTransaction = (transaction, index) => {
  const currencyCode = firstValue(
    transaction?.currency?.code,
    transaction?.currency?.currency_code,
    transaction?.currencyCode,
    transaction?.currency,
    ""
  );

  const dateValue = firstValue(
    transaction?.date,
    transaction?.createdAt,
    transaction?.created_at,
    transaction?.transactionDate,
    transaction?.timestamp
  );

  return {
    id: transaction?.id || transaction?._id || `${index}-${dateValue}`,
    entryType: firstValue(transaction?.entrytype, transaction?.entryType, transaction?.transactionType, transaction?.type),
    date: dateValue,
    amount: transaction?.amount ?? transaction?.transactionAmount ?? transaction?.creditAmount ?? transaction?.debitAmount ?? transaction?.value,
    runningBalance:
      transaction?.runingbalance ??
      transaction?.runningbalance ??
      transaction?.runningBalance ??
      transaction?.running_balance ??
      transaction?.balance,
    currencyCode,
    accountNo: firstValue(
      transaction?.accountNo,
      transaction?.account_no,
      transaction?.accountNumber,
      transaction?.wallet?.accountNo,
      transaction?.wallet?.account_no,
      transaction?.wallet?.accountNumber
    ),
    raw: transaction,
  };
};

function TransactionDetailsDialog({ transaction, timezone, onClose }) {
  const intl = useIntl();

  if (!transaction) return null;

  const tone = getTransactionTone(transaction.entryType, intl);

  return (
    <Dialog open={Boolean(transaction)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 900, color: "#10233f" }}>
        {intl.formatMessage({ id: "dashboard.transactions.dialog.title" })}
      </DialogTitle>
      <DialogContent dividers sx={{ background: "#f8fafc" }}>
        <Box sx={{ display: "grid", gap: 1.5 }}>

          <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #e5ebf2", background: "#fff" }} elevation={0}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
              {intl.formatMessage({ id: "dashboard.transactions.columns.date" })}
            </Typography>
            <Typography variant="body1" sx={{ color: "#10233f", fontWeight: 700 }}>
              {formatDateTime(transaction.date, timezone)}
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #e5ebf2", background: "#fff" }} elevation={0}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
              {intl.formatMessage({ id: "dashboard.transactions.columns.type" })}
            </Typography>
            <Typography variant="h6" sx={{ color: tone.color, fontWeight: 900, textTransform: "uppercase" }}>
              {tone.label}
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #e5ebf2", background: "#fff" }} elevation={0}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
              {intl.formatMessage({ id: "dashboard.transactions.columns.amount" })}
            </Typography>
            <Typography variant="body1" sx={{ color: tone.color, fontWeight: 900 }}>
              {formatAmount(transaction.amount, transaction.currencyCode)}
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #e5ebf2", background: "#fff" }} elevation={0}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
              {intl.formatMessage({ id: "dashboard.transactions.columns.balance" })}
            </Typography>
            <Typography variant="body1" sx={{ color: "#10233f", fontWeight: 900 }}>
              {formatAmount(transaction.runningBalance, transaction.currencyCode)}
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #e5ebf2", background: "#fff" }} elevation={0}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
              {intl.formatMessage({ id: "dashboard.transactions.columns.account" })}
            </Typography>
            <Typography variant="body1" sx={{ color: "#10233f", fontWeight: 900 }}>
              {transaction.accountNo || "--"}
            </Typography>
          </Paper>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function TransactionTable({ rows, loading, onViewDetails }) {
  const intl = useIntl();

  const columns = useMemo(
    () => [
      {
        field: "accountNo",
        headerName: intl.formatMessage({ id: "dashboard.transactions.columns.account" }),
        minWidth: 190,
        flex: 1,
        renderCell: (params) => (
          <Box className="pc-account-cell">
            <Box className="pc-account-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M4 9.5H20" strokeWidth="1.9" />
                <path d="M6.5 9.5V18.5" strokeWidth="1.9" />
                <path d="M12 9.5V18.5" strokeWidth="1.9" />
                <path d="M17.5 9.5V18.5" strokeWidth="1.9" />
                <path d="M4 18.5H20" strokeWidth="1.9" />
                <path d="M3.5 6.5H20.5" strokeWidth="1.9" />
              </svg>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 800, color: "#23344d", letterSpacing: "0.01em" }} noWrap>
              {params?.row?.accountNo || "--"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "date",
        headerName: intl.formatMessage({ id: "dashboard.transactions.columns.date" }),
        minWidth: 190,
        flex: 1,
        renderCell: (params) => (
          <Box className="pc-date-cell">
            <svg className="pc-date-icon" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" strokeWidth="1.8" />
              <path d="M7 3.5V7" strokeWidth="1.8" />
              <path d="M17 3.5V7" strokeWidth="1.8" />
              <path d="M3.5 10H20.5" strokeWidth="1.8" />
            </svg>
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#23344d" }} noWrap>
              {formatDateTime(params?.row?.date, params?.row?.timezone)}
            </Typography>
          </Box>
        ),
      },
       {
        field: "entryType",
        headerName: intl.formatMessage({ id: "dashboard.transactions.columns.type" }),
        minWidth: 150,
        flex: 0.8,
        renderCell: (params) => {
          const tone = getTransactionTone(params?.row?.entryType, intl);
          return (
            <Box className={`pc-type-pill pc-type-pill-${tone.direction || "neutral"}`}>
              <Typography variant="body2" sx={{ fontWeight: 900, color: tone.color, textTransform: "uppercase" }} noWrap>
                {tone.label}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "amount",
        headerName: intl.formatMessage({ id: "dashboard.transactions.columns.amount" }),
        minWidth: 190,
        flex: 1,
        renderCell: (params) => {
          const tone = getTransactionTone(params?.row?.entryType, intl);
          const isDebit = tone.direction === "down";
          return (
            <Box className="pc-amount-cell">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 900,
                  color: tone.color,
                  fontSize: 15,
                  lineHeight: 1.1,
                }}
                noWrap
              >
                {formatAmount(params?.row?.amount, params?.row?.currencyCode)}

                <Box className={`pc-amount-arrow pc-amount-arrow-${isDebit ? "down" : "up"}`} aria-hidden="true">
                {isDebit ? (
                  <svg viewBox="0 0 24 24">
                    <path d="M12 4.5V19.5" strokeWidth="2.1" />
                    <path d="M7.5 14.5L12 19.5L16.5 14.5" strokeWidth="2.1" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24">
                    <path d="M12 19.5V4.5" strokeWidth="2.1" />
                    <path d="M7.5 9.5L12 4.5L16.5 9.5" strokeWidth="2.1" />
                  </svg>
                )}
              </Box>
              </Typography>
              
            </Box>
          );
        },
      },
      {
        field: "runningBalance",
        headerName: intl.formatMessage({ id: "dashboard.transactions.columns.balance" }),
        minWidth: 190,
        flex: 1,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#23344d" }} noWrap>
            {formatAmount(params?.row?.runningBalance, params?.row?.currencyCode)}
          </Typography>
        ),
      },
      {
        field: "details",
        headerName: intl.formatMessage({ id: "dashboard.transactions.columns.actions" }),
        minWidth: 200,
        flex: 0.95,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box className="pc-actions-cell">

            <Button
              type="button"
              variant="text"
              aria-label={intl.formatMessage({ id: "dashboard.transactions.actions.viewDetails" })}
              onClick={() => onViewDetails(params?.row)}
              sx={{
                minWidth: 34,
                width: 34,
                height: 34,
                p: 0,
                borderRadius: '50%',
                color: '#0b7a45',
                background: '#f4fbf6',
                border: '1px solid #cdebd8',
                boxShadow: 'none',
                '&:hover': {
                  background: '#eaf8ef',
                  boxShadow: 'none',
                },
              }}
            >
              <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
                <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.9" />
              </svg>
            </Button>
           
            <Button
              type="button"
              variant="text"
              aria-label={intl.formatMessage({ id: "dashboard.transactions.actions.download" })}
              sx={{
                minWidth: 34,
                width: 34,
                height: 34,
                p: 0,
                borderRadius: '50%',
                color: '#6b7a8b',
                background: '#f6f9fc',
                border: '1px solid #dbe4ec',
                boxShadow: 'none',
                '&:hover': {
                  background: '#edf3f8',
                  boxShadow: 'none',
                },
              }}
            >
              <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
                <path d="M12 3.5V14.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M7.5 10.5L12 15L16.5 10.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M4.5 19.5H19.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </Button>
          </Box>
        ),
      },
    ],
    [onViewDetails, intl]
  );

  return (
    <Paper
      elevation={0}
      className="pc-table-card"
      sx={{
        borderRadius: 4,
        border: "1px solid rgba(15, 39, 72, 0.08)",
        background: "#fff",
        overflow: "hidden",
        mt: 2.5,
      }}
    >
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        loading={loading}
        disableSelectionOnClick
        disableColumnMenu
        density="standard"
        pagination
        pageSize={5}
        rowsPerPageOptions={[5, 10, 25, 50]}
        rowHeight={72}
        headerHeight={60}
        getRowId={(row) => row.id}
        localeText={{ noRowsLabel: intl.formatMessage({ id: "dashboard.transactions.table.noRows" }) }}
        className="pc-data-grid-table"
      />
    </Paper>
  );
}
export default function PaiementsClient() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const token = useSelector((state) => state?.AuthReducer?.token || localStorage.getItem("token") || "");
  const timezone = useSelector((state) => state?.timezone?.timezone || "Africa/Douala");
  const lewooPayToken = useSelector((state) => state?.AuthReducer?.lewooPayToken || localStorage.getItem("lewooPayToken") || "");
  const resolvedToken = String(lewooPayToken || token || "").trim();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState("");

  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadTransactions = async () => {
      if (!resolvedToken) {
        if (isMounted) {
          setTransactions([]);
          setPageError(intl.formatMessage({ id: "dashboard.transactions.errors.loginRequired" }));
        }
        return;
      }

      if (isMounted) {
        setLoading(true);
        setPageError("");
        dispatch(showLoader(intl.formatMessage({ id: "dashboard.transactions.loading" })));
      }

      try {
        const walletsResponse = await WalletApi.getClientWallets(resolvedToken);
        const wallets = walletsResponse?.data?.data || [];
        const primaryWallet = wallets.find((wallet) => wallet.is_primary) || wallets[0] || null;
        const walletId = primaryWallet?.id || primaryWallet?._id || null;

        if (!walletId) {
          throw new Error(intl.formatMessage({ id: "dashboard.transactions.errors.noWallet" }));
        }

        const walletAccountNo = firstValue(primaryWallet?.accountNo, primaryWallet?.account_no, primaryWallet?.accountNumber, walletId);
        const transactionsResponse = await WalletApi.getClientWalletTransactions(walletId, resolvedToken);
        const rawTransactions = transactionsResponse?.data?.data || transactionsResponse?.data?.transactions || transactionsResponse?.data || [];

        const normalizedTransactions = Array.isArray(rawTransactions)
          ? rawTransactions.map((transaction, index) => ({
              ...normalizeTransaction(transaction, index),
              timezone,
              accountNo: firstValue(
                transaction?.accountNo,
                transaction?.account_no,
                transaction?.accountNumber,
                walletAccountNo,
                walletId
              ),
            }))
          : [];

        if (isMounted) {
          setTransactions(normalizedTransactions);

        }
      } catch (error) {
        if (isMounted) {
          const fallbackMessage = intl.formatMessage({ id: "dashboard.transactions.errors.loadFailed" });
          setTransactions([]);
          setPageError(error?.response?.data?.message || error?.message || fallbackMessage);
          dispatch(
            showSnackbar({
              message: error?.response?.data?.message || error?.message || fallbackMessage,
              autoHideDuration: 3000,
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "error",
            })
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          dispatch(hideLoader());
        }
      }
    };

    loadTransactions();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, resolvedToken, timezone]);

  const totalTransactions = transactions.length;

  return (
    <>
      <Paper elevation={0} className="pc-hero-card">
        <Box className="pc-hero-content">
          <Box className="pc-hero-copy">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2.5,
              }}
            >
              <Box
                className="pc-hero-icon"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24"> <rect x="3.5" y="5.5" width="17" height="13" rx="2.5" strokeWidth="1.8" /> <path d="M3.5 10.5H20.5" strokeWidth="1.8" /> <path d="M16.5 14.5H18.5" strokeWidth="1.8" /> <path d="M15.5 8.5H18.5" strokeWidth="1.8" /> </svg>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h3" className="pc-hero-title">
                  {intl.formatMessage({ id: "dashboard.transactions.hero.title" })}
                </Typography>

                <Typography variant="body1" className="pc-hero-subtitle">
                  {intl.formatMessage({ id: "dashboard.transactions.hero.subtitle" })}
                </Typography>
              </Box>
            </Box>

            {/* Bloc bouton refresh laissé commenté, inchangé */}

            <Box className="pc-hero-meta">
              <span className="pc-hero-dot" />
              <span>{intl.formatMessage({ id: "dashboard.transactions.hero.meta" }, { count: totalTransactions })}</span>
            </Box>
          </Box>

          <Box className="pc-hero-art" aria-hidden="true">
            <span className="pc-hero-art-circle pc-hero-art-circle-1" />
            <span className="pc-hero-art-circle pc-hero-art-circle-2" />
            <span className="pc-hero-art-wallet">
              <span className="pc-hero-art-wallet-card" />
              <span className="pc-hero-art-wallet-strap" />
              <span className="pc-hero-art-wallet-button" />
            </span>
            <span className="pc-hero-art-coin" />
            <span className="pc-hero-art-coin pc-hero-art-coin-2" />
          </Box>
        </Box>
      </Paper>
      {pageError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {pageError}
        </Alert>
      ) : null}

      {loading ? (
        <Paper
          elevation={0}
          sx={{
            mt: 2.5,
            p: 4,
            borderRadius: 4,
            border: "1px solid rgba(15, 39, 72, 0.08)",
            display: "grid",
            placeItems: "center",
            gap: 2,
            background: "#fff",
          }}
        >
          <CircularProgress />
          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 700 }}>
            {intl.formatMessage({ id: "dashboard.transactions.loading" })}
          </Typography>
        </Paper>
      ) : (
        <TransactionTable rows={transactions} loading={loading} onViewDetails={setSelectedTransaction} />
      )}

      <TransactionDetailsDialog
        transaction={selectedTransaction}
        timezone={timezone}
        onClose={() => setSelectedTransaction(null)}
      />
    </>
  );
}